import { Mesh, ShaderMaterial, Vector2, Vector4 } from 'three';

import gsap from 'gsap';

import fragmentShader from '../../../../shaders/split-slider-frag.glsl';
import vertexShader from '../../../../shaders/image-vert.glsl';

import { map } from '../../../helpers';

export default class SplitSlider extends Mesh {

	constructor({
		el,
		type,
		assets,
		sizes,
		controls,
		geometry,
		isPreload
	}) {
		super();

		this.isPreload = isPreload;

		this.el = el;
		this.type = type;
		this.controls = controls;
		this.assets = assets;
		this.sizes = sizes;

		// TODO: Loop through all asset bounds
		this.bounds = this.assets[0].image.bounds;

		this.textures = [
			this.assets[0].texture,
			this.assets[1].texture
		];

		this.scroll = 0;

		this.geometry = geometry;

		this.visible = false;

		this.createMaterial();
		this.addEventListeners();
	}

	/**
	 * Listeners
	 */

	addEventListeners() {
		this.el.addEventListener('splitFeature:click', this.#onControlClick);
	}

	removeEventListeners() {
		this.el.removeEventListener('splitFeature:click', this.#onControlClick);
	}

	/**
	 * Events
	 */

	#onControlClick = (e) => {
		const index = e.detail;
		this.isAnimating = true;
		this.timelineTransition = gsap.timeline();
		this.timelineTransition
			.to(this.material.uniforms.progress, {
				ease: 'expo.inOut',
				duration: 1.25,
				value: index
			}, 'start')
			.call(() => {
				this.isAnimating = false;
			});
	}

	/**
	 * Create
	 */

	createMaterial() {
		const imageAspect = this.bounds.imageHeight / this.bounds.imageWidth;
		let a1;
		let a2;

		if (this.bounds.height / this.bounds.width > imageAspect) {
			a1 = (this.bounds.width / this.bounds.height) * imageAspect;
			a2 = 1;
		} else {
			a1 = 1;
			a2 = this.bounds.height / this.bounds.width / imageAspect;
		}

		this.material = new ShaderMaterial({
			depthTest: false,
			depthWrite: false,
			uniforms: {
				image: {
					value: this.textures[0]
				},
				image2: {
					value: this.textures[1]
				},
				imageResolution: {
					type: 'v4',
					value: new Vector4(this.bounds.width, this.bounds.height, a1, a2)
				},
				resolution: {
					type: 'v2',
					value: new Vector2(this.sizes.screen.width, this.sizes.screen.height)
				},
				alpha: {
					value: 0
				},
				progress: {
					value: 0
				},
				parallax: {
					value: 0
				}
			},
			transparent: true,
			fragmentShader,
			vertexShader
		});

		this.scale.set(this.bounds.width, this.bounds.height, this.bounds.width / 2);
		this.position.y = window.pageYOffset || document.documentElement.scrollTop + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
		this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;
	}

	update(scroll) {
		this.scroll = scroll;

		if (this.scroll + this.sizes.screen.height >= this.bounds.top
			&& this.scroll <= this.bounds.top + this.bounds.height) {

			if (!this.visible) this.visible = true;

			this.position.y = scroll + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
			this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;

			const offset = (this.sizes.screen.height - this.bounds.height) * 0.5;

			this.position.y += map(
				this.scroll + offset,
				this.bounds.top,
				this.bounds.height + this.bounds.top,
				0,
				200
			);

			this.material.uniforms.parallax.value = map(
				this.scroll + offset,
				this.bounds.top,
				this.bounds.height + this.bounds.top,
				0, 1
			);
		} else if (this.visible) this.visible = false;
	}

	resize({ sizes, bounds }) {
		this.sizes = sizes;
		this.bounds = bounds;

		this.scale.set(this.bounds.width, this.bounds.height, this.bounds.width / 2);
		this.position.y = this.scroll + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
		this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;
	}

	/**
	 * Animations.
	 */

	show() {
		this.isAnimating = true;

		this.timelineIn = gsap.timeline();
		this.timelineIn
			.to(this.material.uniforms.alpha, {
				ease: 'power4.inOut',
				duration: 0.5,
				value: 1
			}, 'start')
			.call(() => {
				this.isAnimating = false;
			});
	}

	hide(onComplete) {
		return new Promise(resolve => {
			this.isAnimating = true;

			this.timelineOut = gsap.timeline({
				onComplete
			});

			this.timelineOut
				.to(this.material.uniforms.alpha, {
					ease: 'power4.inOut',
					duration: 0.5,
					value: 0
				}, 'start')
				.call(() => {
					this.isAnimating = false;
					resolve();
				});
		});
	}

	/**
	 * Destroy.
	 */

	destroyGeometry() {
		if (this.geometry) {
			this.geometry.dispose();
		}
	}

	destroyMaterial() {
		if (this.material) {
			this.material.dispose();
		}
	}

	destroy() {
		this.destroyGeometry();
		this.destroyMaterial();
		this.removeEventListeners();
	}

}
